import { Box, Paper, Grid, FormControl, InputLabel, Select, MenuItem, Chip, Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import AdminSearchBar from '../shared/AdminSearchBar';
import { UserFilters as UserFiltersType } from '../../../types/admin';

interface UserFiltersProps {
  filters: UserFiltersType;
  onSearchChange: (search: string) => void;
  onFilterChange: (filters: Partial<UserFiltersType>) => void;
  onClearFilters: () => void;
}

/**
 * UserFilters - Search and filter controls for user management
 *
 * Features:
 * - Search by name or email
 * - Filter by subscription tier
 * - Filter by status (active/banned)
 * - Filter by account type (regular/business/agent/has_agent)
 * - Active filter chips display
 * - Clear all filters button
 */
export default function UserFilters({
  filters,
  onSearchChange,
  onFilterChange,
  onClearFilters,
}: UserFiltersProps) {
  const hasActiveFilters = Boolean(
    filters.search || filters.tier || filters.status || filters.type
  );

  const handleRemoveFilter = (filterKey: keyof UserFiltersType) => {
    onFilterChange({ [filterKey]: undefined });
  };

  const getFilterLabel = (key: keyof UserFiltersType, value: string): string => {
    const labels: Record<string, Record<string, string>> = {
      tier: {
        free: 'Free Tier',
        pro: 'Pro Tier',
        agent: 'Agent Tier',
      },
      status: {
        active: 'Active',
        banned: 'Banned',
      },
      type: {
        regular: 'Regular Users',
        business: 'Business Owners',
        agent: 'Agents',
        has_agent: 'Has Agent',
      },
    };

    return labels[key]?.[value] || value;
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FilterListIcon color="action" />
        <Box sx={{ fontWeight: 500 }}>Search & Filters</Box>
      </Box>

      <AdminSearchBar
        value={filters.search || ''}
        onChange={onSearchChange}
        placeholder="Search by name or email..."
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Subscription Tier</InputLabel>
            <Select
              value={filters.tier || ''}
              label="Subscription Tier"
              onChange={(e) => onFilterChange({ tier: (e.target.value as 'free' | 'pro' | 'agent') || undefined })}
            >
              <MenuItem value="">All Tiers</MenuItem>
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="agent">Agent</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e) => onFilterChange({ status: (e.target.value as 'active' | 'banned') || undefined })}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="banned">Banned</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Account Type</InputLabel>
            <Select
              value={filters.type || ''}
              label="Account Type"
              onChange={(e) => onFilterChange({ type: (e.target.value as 'regular' | 'business' | 'agent' | 'has_agent') || undefined })}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="regular">Regular Users</MenuItem>
              <MenuItem value="business">Business Owners</MenuItem>
              <MenuItem value="agent">Agents</MenuItem>
              <MenuItem value="has_agent">Has Agent</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {hasActiveFilters && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>Active Filters:</Box>

          {filters.tier && (
            <Chip
              label={getFilterLabel('tier', filters.tier)}
              onDelete={() => handleRemoveFilter('tier')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {filters.status && (
            <Chip
              label={getFilterLabel('status', filters.status)}
              onDelete={() => handleRemoveFilter('status')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {filters.type && (
            <Chip
              label={getFilterLabel('type', filters.type)}
              onDelete={() => handleRemoveFilter('type')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {filters.search && (
            <Chip
              label={`Search: "${filters.search}"`}
              onDelete={() => handleRemoveFilter('search')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        </Box>
      )}
    </Paper>
  );
}
